import React from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { GreenLines } from "./GreenLines";
import { YellowArrow2 } from "./YellowArrow2";
import { PurpleScribble } from "./PurpleScribble";
import { YellowArrow } from "./YellowArrow";

interface JobGearItemContentProps {
  item: {
    id: string;
    name: string;
    description: string;
    displayImage: {
      url: string;
      alt?: string;
      width?: number;
      height?: number;
    };
  };
  currentIndex: number;
  totalItems: number;
  onNext: () => void;
  onPrevious: () => void;
  loading?: boolean;
}

const ItemContainer = styled(Box, {
  label: "JobGearItemContainer",
})(() => ({
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  height: "691px",
  width: "100%",
  position: "relative",
}));

const ContentArea = styled(Box, {
  label: "JobGearItemContentArea",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  gap: theme.spacing(8),
  height: "691px",
  justifyContent: "center",
}));

const MainContent = styled(Box, {
  label: "JobGearItemMainContent",
})(({ theme }) => ({
  display: "flex",
  overflow: "visible",
  alignItems: "center",
  marginTop: 130,
  gap: theme.spacing(6),
  flex: 1,
  width: "100%",
}));

const TextSection = styled(Box, {
  label: "JobGearItemTextSection",
})<{ isLandscape?: boolean }>(({ theme, isLandscape }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  justifyContent: "center",
  position: "relative",
  borderRadius: theme.spacing(4),
  padding: theme.spacing(0),
  ...(isLandscape ? { width: "453px", flex: "0 0 453px" } : { flex: 1 }),
  "@media (max-width: 1200px)": {
    ...(isLandscape ? { width: "350px", flex: "0 0 350px" } : {}),
  },
  "@media (max-width: 900px)": {
    ...(isLandscape ? { width: "300px", flex: "0 0 300px" } : {}),
  },
}));

const ImageSection = styled(Box, {
  label: "JobGearItemImageSection",
})<{ isLandscape?: boolean }>(({ isLandscape }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "visible",
  ...(isLandscape ? { flex: 1 } : { flex: "0 0 auto" }),
}));

const ItemTitle = styled(Typography, {
  label: "JobGearItemTitle",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "48px",
  lineHeight: "64px",
  color: "#ffffff",
  textAlign: "left",
  "@media (max-width: 1200px)": {
    fontSize: "36px",
    lineHeight: "48px",
  },
  "@media (max-width: 900px)": {
    fontSize: "28px",
    lineHeight: "36px",
  },
}));

const ItemDescription = styled(Typography, {
  label: "JobGearItemDescription",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 400,
  fontSize: "24px",
  lineHeight: "36px",
  color: "#ffffff",
  minWidth: "100%",
  "@media (max-width: 1200px)": {
    fontSize: "20px",
    lineHeight: "30px",
  },
  "@media (max-width: 900px)": {
    fontSize: "16px",
    lineHeight: "24px",
  },
}));

const NavigationSection = styled(Box, {
  label: "JobGearItemNavigation",
})(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}));

const GearImage = styled("img", {
  label: "JobGearImage",
})(() => ({
  maxWidth: 800,
  maxHeight: 500,
  objectFit: "cover",
  borderRadius: 16,
  border: "1px solid rgba(145, 158, 171, 0.1)",
}));

const NavButton = styled(Button, {
  label: "JobGearNavButton",
  shouldForwardProp: (prop) => prop !== "navVariant",
})<{ navVariant?: "back" | "next" | "complete" }>(({ theme, navVariant }) => ({
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: "64px",
  height: "48px",
  textTransform: "none",
  ...(navVariant === "back" && {
    backgroundColor: "#ffffff",
    color: "#1c252e",
    border: "1px solid rgba(145, 158, 171, 0.1)",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  }),
  ...(navVariant === "next" && {
    backgroundColor: "#078dee",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#0570c4",
    },
  }),
  ...(navVariant === "complete" && {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
}));

const parseDescription = (descriptionJson: string | unknown): string => {
  try {
    if (typeof descriptionJson === "string") {
      const parsed = JSON.parse(descriptionJson);
      if (Array.isArray(parsed) && parsed[0]?.children) {
        return parsed[0].children
          .map((child: { text?: string }) => child.text)
          .join(" ");
      }
    }
    return typeof descriptionJson === "string" ? descriptionJson : "";
  } catch {
    return (descriptionJson as string) || "";
  }
};

const ImageDecorations = ({ isLandscape }: { isLandscape: boolean }) => {
  if (isLandscape) {
    return (
      <>
        <GreenLines style={{ position: "absolute", left: -60, top: -80 }} />
        <YellowArrow2
          style={{ position: "absolute", right: -60, bottom: 10 }}
        />
      </>
    );
  }

  return <YellowArrow style={{ position: "absolute", right: -30, top: -10 }} />;
};

export const JobGearItemContent = ({
  item,
  currentIndex,
  totalItems,
  onNext,
  onPrevious,
  loading,
}: JobGearItemContentProps) => {
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalItems - 1;

  const description = parseDescription(item.description);

  const imageWidth = item.displayImage?.width || 0;
  const imageHeight = item.displayImage?.height || 0;
  const isLandscape = imageWidth > imageHeight;

  return (
    <ItemContainer>
      {isLandscape || (
        <PurpleScribble
          style={{
            position: "fixed",
            left: 0,
            bottom: 0,
          }}
        />
      )}
      <ContentArea>
        <MainContent>
          <TextSection isLandscape={isLandscape}>
            <ItemTitle>{item.name}</ItemTitle>
            <ItemDescription>{description}</ItemDescription>
          </TextSection>

          <ImageSection isLandscape={isLandscape}>
            <GearImage
              src={item.displayImage?.url}
              alt={item.displayImage?.alt || item.name}
            />
            <ImageDecorations isLandscape={isLandscape} />
          </ImageSection>
        </MainContent>

        <NavigationSection>
          <NavButton
            navVariant="back"
            onClick={onPrevious}
            disabled={isFirst}
            sx={{ visibility: isFirst ? "hidden" : "visible" }}
          >
            Back
          </NavButton>

          {isLast ? (
            <NavButton
              disabled={loading}
              navVariant="complete"
              onClick={onNext}
            >
              Complete
            </NavButton>
          ) : (
            <NavButton disabled={loading} navVariant="next" onClick={onNext}>
              Next
            </NavButton>
          )}
        </NavigationSection>
      </ContentArea>
    </ItemContainer>
  );
};
